import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

// ============================================================================
// COMMUNITIES HUB - Main landing page for community discovery and management
// ============================================================================

const LOCAL_STORAGE_KEY = 'aiia_community_visited';

const MembersList = ({ communityId }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setLoading(true);
        const res = await apiService.getCommunityMembers(communityId);
        setMembers(res.members || []);
      } catch (err) {
        console.error("Failed to load members", err);
      } finally {
        setLoading(false);
      }
    };
    if (communityId) fetchMembers();
  }, [communityId]);

  if (loading) return <div className="p-4 text-slate-500 text-sm animate-pulse">Loading members...</div>;

  return (
    <div className="h-full overflow-y-auto p-2 space-y-1 custom-scrollbar">
      <h3 className="text-xs font-bold text-slate-400 uppercase mb-3 px-2 tracking-wider">Members — {members.length}</h3>
      {members.map(m => (
        <div key={m.id} className="flex items-center gap-3 px-2 py-1.5 hover:bg-white/5 rounded-lg cursor-pointer group transition-colors">
          <div className="relative shrink-0">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden ring-2 ring-transparent group-hover:ring-white/10 transition-all">
              {m.avatar_url ? <img src={m.avatar_url} className="w-full h-full object-cover" /> : (m.username?.[0] || '?')}
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-950 ${m.is_online ? 'bg-emerald-500' : 'bg-slate-500'}`} />
          </div>
          <div className="overflow-hidden min-w-0">
            <div className="text-sm text-slate-200 truncate font-medium flex items-center gap-1">
              {m.username}
              {m.role === 'admin' && <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1 rounded border border-amber-500/30">OWNER</span>}
              {m.role === 'moderator' && <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1 rounded border border-blue-500/30">MOD</span>}
            </div>
            <div className="text-xs text-slate-500 truncate">{m.status_message || (m.role === 'admin' ? 'Community Admin' : 'Member')}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

const formatMessage = (content) => {
  if (!content) return null;
  
  // Code blocks
  if (content.includes('```')) {
    const parts = content.split('```');
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        return <pre key={i} className="bg-black/50 p-3 rounded-lg my-2 overflow-x-auto text-xs font-mono text-emerald-300 border border-white/10">{part}</pre>;
      }
      return <span key={i}>{formatMessage(part)}</span>;
    });
  }

  // Links
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = content.split(urlRegex);
  
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 hover:underline break-all transition-colors">{part}</a>;
    }
    // Bold
    if (part.includes('**')) {
        const boldParts = part.split('**');
        return boldParts.map((bp, j) => j % 2 === 1 ? <strong key={`${i}-${j}`} className="text-white font-bold">{bp}</strong> : bp);
    }
    // Italic
    if (part.includes('*')) {
        const italicParts = part.split('*');
        return italicParts.map((ip, j) => j % 2 === 1 ? <em key={`${i}-${j}`} className="italic text-slate-300">{ip}</em> : ip);
    }
    return part;
  });
};

const ChannelChat = ({ channelId, user }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const history = await apiService.getMessages(channelId, { limit: 50 });
        // Normalize history messages to match socket format
        const normalized = history.map(m => ({
          id: m.id,
          channelId: m.channel_id,
          content: m.content,
          userId: m.user_id,
          username: m.username,
          avatarUrl: m.avatar_url,
          timestamp: m.created_at,
          attachmentUrl: m.attachment_url,
          attachmentType: m.attachment_type
        })).reverse(); // Oldest first
        setMessages(normalized);
        scrollToBottom();
      } catch (err) {
        console.error('Failed to load channel history:', err);
      }
    };
    if (channelId) loadHistory();
  }, [channelId]);

  useEffect(() => {
    // Connect to socket
    const socket = io(apiService.baseURL || '/', {
      path: '/socket.io',
      auth: {
        token: apiService.authHandlers.getAccessToken()
      }
    });
    socketRef.current = socket;

    socket.emit('chat:join', channelId);

    socket.on('chat:message', (msg) => {
      // Double check channel ID to prevent leaks
      if (msg.channelId && msg.channelId !== channelId) return;
      
      setMessages(prev => [...prev, msg]);
      scrollToBottom();
    });

    return () => {
      socket.emit('chat:leave', channelId);
      socket.disconnect();
    };
  }, [channelId]);

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'file';
      const res = await apiService.uploadFile(file, type);
      
      const msg = {
        channelId,
        content: '', 
        userId: user.id,
        username: user.username,
        avatarUrl: user.avatar_url,
        timestamp: new Date().toISOString(),
        attachmentUrl: res.url,
        attachmentType: type
      };
      socketRef.current.emit('chat:message', msg);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const msg = {
      channelId,
      content: input,
      userId: user.id,
      username: user.username,
      avatarUrl: user.avatar_url,
      timestamp: new Date().toISOString()
    };

    socketRef.current.emit('chat:message', msg);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white/5 rounded-2xl border border-white/10 overflow-hidden shadow-inner">
      <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar">
        {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 opacity-50">
                <div className="text-6xl mb-4">💬</div>
                <p className="text-lg font-medium">Start the conversation!</p>
                <p className="text-sm">Be the first to say hello.</p>
            </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-4 ${msg.userId === user.id ? 'flex-row-reverse' : ''} group animate-in fade-in slide-in-from-bottom-2 duration-300`}>
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden shrink-0 mt-1 ring-2 ring-transparent group-hover:ring-white/10 transition-all">
              {msg.avatarUrl ? <img src={msg.avatarUrl} className="w-full h-full object-cover" /> : (msg.username?.[0] || '?')}
            </div>
            <div className={`max-w-[80%] flex flex-col ${msg.userId === user.id ? 'items-end' : 'items-start'}`}>
                <div className="flex items-baseline gap-2 mb-1 px-1">
                    <span className="text-sm font-bold text-slate-200">{msg.username}</span>
                    <span className="text-[10px] text-slate-500">{new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                </div>
                <div className={`px-4 py-3 rounded-2xl text-[15px] leading-relaxed shadow-sm ${msg.userId === user.id ? 'bg-emerald-600 text-white rounded-tr-none' : 'bg-white/10 text-slate-200 rounded-tl-none'}`}>
                  {msg.attachmentUrl && (
                    <div className="mb-2 rounded-lg overflow-hidden">
                      {msg.attachmentType === 'image' ? (
                        <img src={msg.attachmentUrl} alt="Attachment" className="max-w-full max-h-64 object-cover" />
                      ) : msg.attachmentType === 'video' ? (
                        <video src={msg.attachmentUrl} controls className="max-w-full max-h-64" />
                      ) : (
                        <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-emerald-200 hover:underline">
                          📎 Download File
                        </a>
                      )}
                    </div>
                  )}
                  {msg.content && formatMessage(msg.content)}
                </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={sendMessage} className="p-4 border-t border-white/10 bg-black/20">
        <div className="relative flex gap-2 items-end">
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept="image/*,video/*"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-slate-400 hover:text-white transition-colors"
              title="Upload Image/Video"
            >
              {uploading ? '⏳' : '📎'}
            </button>
            <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-emerald-500 text-white resize-none custom-scrollbar transition-all focus:bg-white/10"
            placeholder="Message #channel..."
            rows={1}
            style={{ minHeight: '50px', maxHeight: '150px' }}
            />
            <button 
                type="submit" 
                disabled={!input.trim()}
                className="p-3 bg-emerald-500 text-black rounded-xl hover:bg-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
            >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
            </svg>
            </button>
        </div>
        <div className="text-[10px] text-slate-500 mt-2 flex gap-4 px-2 font-mono opacity-70">
            <span title="Bold">**bold**</span>
            <span title="Italic">*italic*</span>
            <span title="Code Block">```code```</span>
        </div>
      </form>
    </div>
  );
};

/**
 * First-time visitor welcome screen with create/browse options
 */
function WelcomeScreen({ onCreateCommunity, onBrowseCommunities }) {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-3xl flex items-center justify-center text-5xl mb-6">
            🏛️
          </div>
          <h1 className="text-4xl font-black text-white mb-4">Welcome to Communities</h1>
          <p className="text-xl text-slate-400">
            Build your creative empire or join forces with fellow creators.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Create Community Card */}
          <button
            onClick={onCreateCommunity}
            className="group bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 hover:border-emerald-400 rounded-3xl p-8 text-left transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-emerald-500/10"
          >
            <div className="w-16 h-16 bg-emerald-500/20 rounded-2xl flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform">
              ✨
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Create Community</h2>
            <p className="text-slate-400">
              Start your own creative hub. Build channels, set roles, invite collaborators.
            </p>
          </button>

          {/* Browse Communities Card */}
          <button
            onClick={onBrowseCommunities}
            className="group bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 hover:border-purple-400 rounded-3xl p-8 text-left transition-all hover:scale-[1.02] hover:shadow-xl hover:shadow-purple-500/10"
          >
            <div className="w-16 h-16 bg-purple-500/20 rounded-2xl flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform">
              🔍
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Browse Communities</h2>
            <p className="text-slate-400">
              Explore active communities, find your tribe, and join the action.
            </p>
          </button>
        </div>

        <p className="text-sm text-slate-500">
          You can always create or join communities later from the hub.
        </p>
      </div>
    </div>
  );
}

/**
 * Community card for displaying community info
 */
function CommunityCard({ community, onClick, isMember = false }) {
  return (
    <button
      onClick={() => onClick(community)}
      className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl p-5 text-left transition-all w-full"
    >
      <div className="flex items-start gap-4">
        {community.icon_url ? (
          <img
            src={community.icon_url}
            alt={community.name}
            className="w-14 h-14 rounded-xl object-cover"
          />
        ) : (
          <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center text-2xl font-bold text-white">
            {community.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-white truncate">{community.name}</h3>
            {community.display_tag && (
              <span className="text-xs text-emerald-400 font-mono">{community.display_tag}</span>
            )}
          </div>
          <p className="text-sm text-slate-400 line-clamp-2 mb-2">
            {community.description || 'No description'}
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              👥 {community.member_count || 0} members
            </span>
            {isMember && (
              <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">
                Joined
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Free Agent Card for recruiting section
 */
function FreeAgentCard({ user, onInvite, isInvited }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/[0.07] transition-colors">
      <div className="flex items-center gap-3 mb-3">
        {user.avatar_url ? (
          <img src={user.avatar_url} alt={user.username} className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-lg font-bold text-white">
            {user.username?.charAt(0)?.toUpperCase() || '?'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-white truncate">{user.display_name || user.username}</h4>
          <p className="text-sm text-slate-400 truncate">@{user.username}</p>
        </div>
      </div>
      
      {/* Skills Preview */}
      {user.skills && user.skills.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {user.skills.slice(0, 3).map((skill, i) => (
            <span key={i} className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs">
              {skill}
            </span>
          ))}
          {user.skills.length > 3 && (
            <span className="px-2 py-0.5 bg-white/10 text-slate-400 rounded text-xs">
              +{user.skills.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
        {user.follower_count !== undefined && (
          <span>👥 {user.follower_count} followers</span>
        )}
        {user.post_count !== undefined && (
          <span>📹 {user.post_count} posts</span>
        )}
      </div>

      <button
        onClick={() => onInvite(user)}
        disabled={isInvited}
        className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
          isInvited 
            ? 'bg-emerald-500/20 text-emerald-400 cursor-default'
            : 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-300'
        }`}
      >
        {isInvited ? 'Invited ✓' : 'Invite to Community'}
      </button>
    </div>
  );
}

/**
 * Community Stats Card for detailed view
 */
function CommunityStatsCard({ community }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-white/5 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-emerald-400">{community.member_count || 0}</div>
        <div className="text-sm text-slate-400">Members</div>
      </div>
      <div className="bg-white/5 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-cyan-400">{community.bounties_completed || 0}</div>
        <div className="text-sm text-slate-400">Bounties</div>
      </div>
      <div className="bg-white/5 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-purple-400">{community.total_views || '0'}</div>
        <div className="text-sm text-slate-400">Total Views</div>
      </div>
      <div className="bg-white/5 rounded-xl p-4 text-center">
        <div className="text-2xl font-bold text-pink-400">{community.avg_level || 1}</div>
        <div className="text-sm text-slate-400">Avg Level</div>
      </div>
    </div>
  );
}

/**
 * Main Communities Hub Component
 */
export default function CommunitiesHub() {
  const navigate = useNavigate();
  const { communityId, channelSlug } = useParams();
  const { user, isAuthenticated } = useAuth();
  
  // View state
  const [view, setView] = useState('loading'); // 'loading' | 'welcome' | 'browse' | 'detail'
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('discover'); // 'discover' | 'my-communities' | 'free-agents' | 'mini-games'
  const [activeChannel, setActiveChannel] = useState(null);
  
  // Category state for chunked loading
  const [communityCategories, setCommunityCategories] = useState([]);
  const [agentCategories, setAgentCategories] = useState([]);
  const [selectedCommunityCategory, setSelectedCommunityCategory] = useState('all');
  const [selectedAgentCategory, setSelectedAgentCategory] = useState('all');
  
  // Data state
  const [myCommunities, setMyCommunities] = useState([]);
  const [discoverCommunities, setDiscoverCommunities] = useState([]);
  const [freeAgents, setFreeAgents] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invitedUserIds, setInvitedUserIds] = useState(new Set());
  
  // Mini-games state
  const [miniGames, setMiniGames] = useState([]);
  const [gameLeaderboard, setGameLeaderboard] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameSession, setGameSession] = useState(null);
  
  // Pagination state
  const [discoverOffset, setDiscoverOffset] = useState(0);
  const [agentsOffset, setAgentsOffset] = useState(0);
  const [hasMoreCommunities, setHasMoreCommunities] = useState(true);
  const [hasMoreAgents, setHasMoreAgents] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 12;

  // Check if first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem(LOCAL_STORAGE_KEY);
    
    if (communityId) {
      setView('detail');
      loadCommunityDetail(communityId);
    } else if (!hasVisited && isAuthenticated) {
      setView('welcome');
    } else {
      setView('browse');
      loadInitialData();
    }
  }, [communityId, isAuthenticated]);

  // Set active channel when channelSlug changes
  useEffect(() => {
    if (channelSlug && selectedCommunity?.channels) {
      const channel = selectedCommunity.channels.find(
        ch => ch.slug === channelSlug || ch.name?.toLowerCase().replace(/\s+/g, '-') === channelSlug
      );
      setActiveChannel(channel || null);
    }
  }, [channelSlug, selectedCommunity]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setDiscoverOffset(0);
    setAgentsOffset(0);
    setHasMoreCommunities(true);
    setHasMoreAgents(true);
    
    try {
      // Load categories for chunked loading
      try {
        const catRes = await apiService.getCommunityCategories?.() || { categories: [] };
        setCommunityCategories(catRes.categories || []);
      } catch (e) {
        console.warn('Categories not available:', e);
      }
      
      try {
        const agentCatRes = await apiService.getFreeAgentCategories?.() || { categories: [] };
        setAgentCategories(agentCatRes.categories || []);
      } catch (e) {
        console.warn('Agent categories not available:', e);
      }
      
      // Load user's communities (no pagination needed - usually small set)
      if (isAuthenticated && user?.id) {
        try {
          const myRes = await apiService.getUserCommunities(user.id);
          setMyCommunities(myRes.communities || myRes || []);
        } catch (e) {
          console.warn('Failed to load user communities:', e);
        }
      }

      // Load discoverable communities with pagination (respects selected category)
      try {
        const discoverRes = await apiService.getCommunitiesByCategory?.({ 
          category: selectedCommunityCategory, 
          limit: ITEMS_PER_PAGE, 
          offset: 0 
        }) || await apiService.getCommunities({ limit: ITEMS_PER_PAGE, offset: 0 });
        const communities = discoverRes.communities || discoverRes || [];
        setDiscoverCommunities(communities);
        setHasMoreCommunities(communities.length >= ITEMS_PER_PAGE);
        setDiscoverOffset(communities.length);
      } catch (e) {
        console.warn('Failed to load communities:', e);
      }

      // Load free agents with pagination (respects selected category)
      try {
        const agentsRes = await apiService.getFreeAgentsByCategory?.({ 
          category: selectedAgentCategory, 
          limit: ITEMS_PER_PAGE, 
          offset: 0 
        }) || await apiService.getFreeAgents?.({ limit: ITEMS_PER_PAGE, offset: 0 }) || { users: [] };
        const agents = agentsRes.users || [];
        setFreeAgents(agents);
        setHasMoreAgents(agents.length >= ITEMS_PER_PAGE);
        setAgentsOffset(agents.length);
      } catch (e) {
        console.warn('Free agents API not available');
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id, selectedCommunityCategory, selectedAgentCategory]);
  
  // Reload when category changes
  useEffect(() => {
    if (view === 'browse' && !loading) {
      loadInitialData();
    }
  }, [selectedCommunityCategory, selectedAgentCategory]);
  
  // Load more communities
  const loadMoreCommunities = useCallback(async () => {
    if (loadingMore || !hasMoreCommunities) return;
    
    setLoadingMore(true);
    try {
      const res = await apiService.getCommunitiesByCategory?.({ 
        category: selectedCommunityCategory, 
        limit: ITEMS_PER_PAGE, 
        offset: discoverOffset 
      }) || await apiService.getCommunities({ limit: ITEMS_PER_PAGE, offset: discoverOffset });
      const newCommunities = res.communities || res || [];
      setDiscoverCommunities(prev => [...prev, ...newCommunities]);
      setHasMoreCommunities(newCommunities.length >= ITEMS_PER_PAGE);
      setDiscoverOffset(prev => prev + newCommunities.length);
    } catch (e) {
      console.error('Failed to load more communities:', e);
    } finally {
      setLoadingMore(false);
    }
  }, [discoverOffset, hasMoreCommunities, loadingMore, selectedCommunityCategory]);
  
  // Load more free agents
  const loadMoreAgents = useCallback(async () => {
    if (loadingMore || !hasMoreAgents) return;
    
    setLoadingMore(true);
    try {
      const res = await apiService.getFreeAgentsByCategory?.({ 
        category: selectedAgentCategory, 
        limit: ITEMS_PER_PAGE, 
        offset: agentsOffset 
      }) || await apiService.getFreeAgents?.({ limit: ITEMS_PER_PAGE, offset: agentsOffset }) || { users: [] };
      const newAgents = res.users || [];
      setFreeAgents(prev => [...prev, ...newAgents]);
      setHasMoreAgents(newAgents.length >= ITEMS_PER_PAGE);
      setAgentsOffset(prev => prev + newAgents.length);
    } catch (e) {
      console.error('Failed to load more agents:', e);
    } finally {
      setLoadingMore(false);
    }
  }, [agentsOffset, hasMoreAgents, loadingMore, selectedAgentCategory]);

  // Load mini-games catalog
  const loadMiniGames = useCallback(async () => {
    setLoadingGames(true);
    try {
      const res = await apiService.getGamesCatalog?.() || { games: [] };
      setMiniGames(res.games || []);
      
      // Load global leaderboard
      const leaderboardRes = await apiService.getGameLeaderboard?.('global') || { entries: [] };
      setGameLeaderboard(leaderboardRes.entries || []);
    } catch (e) {
      console.error('Failed to load mini-games:', e);
    } finally {
      setLoadingGames(false);
    }
  }, []);
  
  // Load games when switching to mini-games tab
  useEffect(() => {
    if (activeTab === 'mini-games' && miniGames.length === 0) {
      loadMiniGames();
    }
  }, [activeTab, miniGames.length, loadMiniGames]);
  
  // Start a game session
  const startGameSession = useCallback(async (game) => {
    try {
      const res = await apiService.createGameSession?.(game.id) || {};
      setSelectedGame(game);
      setGameSession(res.session || { id: 'local', game_id: game.id });
    } catch (e) {
      console.error('Failed to start game session:', e);
      // Fallback to local game
      setSelectedGame(game);
      setGameSession({ id: 'local', game_id: game.id });
    }
  }, []);
  
  // End game session and submit score
  const endGameSession = useCallback(async (score) => {
    if (gameSession?.id && gameSession.id !== 'local') {
      try {
        await apiService.endGameSession?.(gameSession.id, score);
      } catch (e) {
        console.error('Failed to submit score:', e);
      }
    }
    setSelectedGame(null);
    setGameSession(null);
    loadMiniGames(); // Refresh leaderboard
  }, [gameSession, loadMiniGames]);

  const loadCommunityDetail = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await apiService.getCommunity(id);
      setSelectedCommunity(res.community || res);
    } catch (e) {
      console.error('Failed to load community:', e);
      setView('browse');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      loadInitialData();
      return;
    }

    setLoading(true);
    try {
      const res = await apiService.searchCommunities(query);
      setDiscoverCommunities(res.communities || []);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setLoading(false);
    }
  }, [loadInitialData]);

  const handleCreateCommunity = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    navigate('/communities/create');
  }, [navigate]);

  const handleBrowseCommunities = useCallback(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setView('browse');
    loadInitialData();
  }, [loadInitialData]);

  const handleCommunityClick = useCallback((community) => {
    navigate(`/communities/${community.slug || community.id}`);
  }, [navigate]);

  const handleInviteAgent = useCallback(async (agent) => {
    // Simulate API call
    setInvitedUserIds(prev => {
      const next = new Set(prev);
      next.add(agent.id);
      return next;
    });
    console.log('Invited agent:', agent);
  }, []);

  const handleJoinCommunity = useCallback(async (community) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Verification Step
    const agreed = window.confirm(
      `Join ${community.name}?\n\nVerification Required:\n1. Agree to Community Rules\n2. Be respectful to all members\n3. No spam or self-promotion without permission\n\nDo you agree to these terms?`
    );

    if (!agreed) return;

    try {
      await apiService.joinCommunity(community.id);
      // Refresh data
      loadInitialData();
      if (selectedCommunity?.id === community.id) {
        loadCommunityDetail(community.id);
      }
    } catch (e) {
      console.error('Failed to join community:', e);
    }
  }, [isAuthenticated, navigate, loadInitialData, selectedCommunity, loadCommunityDetail]);

  const handleDeleteCommunity = useCallback(async (communityId) => {
    if (!window.confirm('Are you sure you want to delete this community? This action cannot be undone.')) {
      return;
    }
    try {
      await apiService.deleteCommunity(communityId);
      // Refresh data and go back to browse
      setView('browse');
      setSelectedCommunity(null);
      loadInitialData();
      navigate('/communities');
    } catch (e) {
      console.error('Failed to delete community:', e);
      alert('Failed to delete community');
    }
  }, [navigate, loadInitialData]);

  // Filter communities based on tab
  const displayedCommunities = useMemo(() => {
    if (activeTab === 'my-communities') {
      return myCommunities;
    }
    return discoverCommunities;
  }, [activeTab, myCommunities, discoverCommunities]);

  const myCommunitiesIds = useMemo(() => {
    return new Set(myCommunities.map(c => c.id));
  }, [myCommunities]);

  // Render based on view state
  if (view === 'loading') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (view === 'welcome') {
    return (
      <WelcomeScreen
        onCreateCommunity={handleCreateCommunity}
        onBrowseCommunities={handleBrowseCommunities}
      />
    );
  }

  // Community Detail View
  if (view === 'detail' && selectedCommunity) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        {/* Header with back button */}
        <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setView('browse');
                  setSelectedCommunity(null);
                  navigate('/communities');
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                ← Back
              </button>
              <div className="flex-1">
                <h1 className="text-xl font-bold">{selectedCommunity.name}</h1>
                <p className="text-sm text-slate-400">
                  {selectedCommunity.member_count || 0} members
                </p>
              </div>
              {user?.id && String(user.id) === String(selectedCommunity.creator_id) ? (
                <button
                  onClick={() => handleDeleteCommunity(selectedCommunity.id)}
                  className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/50 font-bold rounded-xl transition-colors"
                >
                  Delete Community
                </button>
              ) : (
                <button
                  onClick={() => handleJoinCommunity(selectedCommunity)}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-colors"
                >
                  Join Community
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Community content */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Sidebar - Channels */}
            <div className="col-span-12 md:col-span-3 space-y-2">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide px-2 mb-3">
                Channels
              </h3>
              {(selectedCommunity.channels?.length > 0 || selectedCommunity.tiers?.length > 0) ? (
                (selectedCommunity.channels || selectedCommunity.tiers.flatMap(t => t.channels || [])).map((channel) => (
                  <button
                    key={channel.id}
                    onClick={() => {
                      setActiveChannel(channel);
                      navigate(`/communities/${communityId}/${channel.slug || channel.name?.toLowerCase().replace(/\s+/g, '-')}`);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      activeChannel?.id === channel.id
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : 'hover:bg-white/5 text-slate-300'
                    }`}
                  >
                    <span>{channel.emoji || '#'}</span>
                    <span className="truncate">{channel.name}</span>
                  </button>
                ))
              ) : (
                <p className="text-sm text-slate-500 px-2">No channels yet</p>
              )}
            </div>

            {/* Main content area */}
            <div className="col-span-12 md:col-span-9">
              {activeChannel ? (
                <div className="flex gap-4 h-[calc(100vh-140px)]">
                    <div className="flex-1 flex flex-col bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                        <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-black/20 shrink-0">
                            <span className="text-2xl">{activeChannel.emoji || '#'}</span>
                            <div>
                            <h2 className="text-xl font-bold">{activeChannel.name}</h2>
                            <p className="text-sm text-slate-400">{activeChannel.topic || 'No topic set'}</p>
                            </div>
                        </div>
                        
                        <div className="flex-1 overflow-hidden">
                            <ChannelChat channelId={activeChannel.id} user={user} />
                        </div>
                    </div>
                    
                    {/* Members List Sidebar - Hidden on mobile, visible on large screens */}
                    <div className="w-64 hidden lg:block bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                        <MembersList communityId={selectedCommunity.id} />
                    </div>
                </div>
              ) : (
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
                  {/* Community overview */}
                  <div className="text-center py-8">
                    <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-4xl mb-4">
                      {selectedCommunity.avatar || selectedCommunity.name?.[0] || '🏠'}
                    </div>
                    <h2 className="text-2xl font-bold mb-2">{selectedCommunity.name}</h2>
                    <p className="text-slate-400 max-w-md mx-auto mb-6">
                      {selectedCommunity.description || 'No description provided'}
                    </p>
                    
                    <div className="flex justify-center gap-6 text-sm text-slate-400">
                      <div>
                        <span className="font-bold text-white">{selectedCommunity.member_count || 0}</span> members
                      </div>
                      <div>
                        <span className="font-bold text-white">{selectedCommunity.channels?.length || 0}</span> channels
                      </div>
                    </div>
                  </div>

                  {/* Select a channel prompt */}
                  <div className="text-center py-8 border-t border-white/10 mt-8">
                    <p className="text-slate-500">Select a channel from the sidebar to start chatting</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Browse/Hub view
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-black">Communities</h1>
            
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search communities..."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 pl-10 focus:border-emerald-500 focus:outline-none transition-colors"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              </div>
            </div>

            {/* Create Button */}
            <button
              onClick={handleCreateCommunity}
              className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-colors flex items-center gap-2"
            >
              <span>+</span>
              <span className="hidden sm:inline">Create</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-white/10 -mb-px overflow-x-auto">
            {[
              { id: 'discover', label: 'Discover', icon: '🔍' },
              { id: 'my-communities', label: 'My Communities', icon: '🏠' },
              { id: 'free-agents', label: 'Free Agents', icon: '🦸' },
              { id: 'mini-games', label: 'Games', icon: '🎮' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'text-emerald-400 border-emerald-400'
                    : 'text-slate-400 border-transparent hover:text-white'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
                {tab.id === 'my-communities' && myCommunities.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">
                    {myCommunities.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : activeTab === 'free-agents' ? (
          /* Free Agents Tab */
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-2">Free Agents</h2>
              <p className="text-slate-400">
                Talented creators looking for communities to join. Invite them to your team!
              </p>
            </div>

            {/* Category Filter Tabs */}
            {agentCategories.length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {agentCategories.map((cat) => (
                  <button
                    key={cat.category}
                    onClick={() => setSelectedAgentCategory(cat.category)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                      selectedAgentCategory === cat.category
                        ? 'bg-emerald-500 text-black'
                        : 'bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {cat.category === 'all' ? '🌐 All' : cat.category === 'unspecified' ? '❓ Other' : `${cat.category}`}
                    <span className="ml-2 text-xs opacity-70">({cat.count})</span>
                  </button>
                ))}
              </div>
            )}

            {freeAgents.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <div className="text-5xl mb-4">🦸</div>
                <p>No free agents available right now.</p>
                <p className="text-sm mt-2">Check back later for talented creators!</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                  {freeAgents.map((agent) => (
                    <FreeAgentCard
                      key={agent.id}
                      user={agent}
                      onInvite={handleInviteAgent}
                      isInvited={invitedUserIds.has(agent.id)}
                    />
                  ))}
                </div>
                
                {/* Load More Button */}
                {hasMoreAgents && (
                  <div className="text-center mb-8">
                    <button
                      onClick={loadMoreAgents}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors disabled:opacity-50"
                    >
                      {loadingMore ? 'Loading...' : 'Load More Agents'}
                    </button>
                  </div>
                )}

                {invitedUserIds.size > 0 && (
                  <div className="border-t border-white/10 pt-6">
                    <h3 className="text-lg font-bold mb-4">Sent Invitations ({invitedUserIds.size})</h3>
                    <div className="flex flex-wrap gap-3">
                      {freeAgents
                        .filter(agent => invitedUserIds.has(agent.id))
                        .map(agent => (
                          <div key={agent.id} className="flex items-center gap-2 bg-white/5 px-3 py-2 rounded-lg border border-white/10">
                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-xs text-emerald-400">
                              {agent.username?.[0] || '?'}
                            </div>
                            <span className="text-sm text-slate-300">{agent.username}</span>
                            <span className="text-xs text-emerald-400 ml-1">Invited</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : activeTab === 'mini-games' ? (
          /* Mini Games Tab */
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-2">🎮 Mini Games</h2>
              <p className="text-slate-400">
                Play games, compete with friends, and earn rewards!
              </p>
            </div>

            {loadingGames ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {(miniGames.length > 0 ? miniGames : [
                  // Fallback static games if API returns empty
                  { id: 'trivia', name: 'Trivia Challenge', description: 'Test your knowledge across various topics', icon: '🧠', xp_reward: 50, color: 'purple' },
                  { id: 'memory', name: 'Memory Match', description: 'Find matching pairs before time runs out', icon: '🃏', xp_reward: 30, color: 'blue' },
                  { id: 'word-scramble', name: 'Word Scramble', description: 'Unscramble words to score points', icon: '📝', xp_reward: 40, color: 'emerald' },
                  { id: 'speed-typing', name: 'Speed Typing', description: 'Race against the clock to type accurately', icon: '⌨️', xp_reward: 60, color: 'amber' },
                  { id: 'reaction', name: 'Reaction Test', description: 'How fast are your reflexes?', icon: '⚡', xp_reward: 25, color: 'red' },
                  { id: 'daily-puzzle', name: 'Daily Puzzle', description: 'New puzzle every day!', icon: '🧩', xp_reward: 100, color: 'indigo' },
                ]).map((game) => {
                  const colorMap = {
                    purple: { gradient: 'from-purple-500/20 to-pink-500/20', border: 'border-purple-500/30', text: 'text-purple-300', btn: 'bg-purple-500/30 hover:bg-purple-500/50' },
                    blue: { gradient: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30', text: 'text-blue-300', btn: 'bg-blue-500/30 hover:bg-blue-500/50' },
                    emerald: { gradient: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/30', text: 'text-emerald-300', btn: 'bg-emerald-500/30 hover:bg-emerald-500/50' },
                    amber: { gradient: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-500/30', text: 'text-amber-300', btn: 'bg-amber-500/30 hover:bg-amber-500/50' },
                    red: { gradient: 'from-red-500/20 to-rose-500/20', border: 'border-red-500/30', text: 'text-red-300', btn: 'bg-red-500/30 hover:bg-red-500/50' },
                    indigo: { gradient: 'from-indigo-500/20 to-violet-500/20', border: 'border-indigo-500/30', text: 'text-indigo-300', btn: 'bg-indigo-500/30 hover:bg-indigo-500/50' },
                  };
                  const colors = colorMap[game.color] || colorMap.purple;
                  return (
                    <div 
                      key={game.id} 
                      className={`bg-gradient-to-br ${colors.gradient} border ${colors.border} rounded-2xl p-6 hover:scale-105 transition-transform cursor-pointer`}
                      onClick={() => startGameSession(game)}
                    >
                      <div className="text-4xl mb-3">{game.icon || '🎮'}</div>
                      <h3 className="font-bold text-lg mb-1">{game.name}</h3>
                      <p className="text-sm text-slate-400 mb-4">{game.description}</p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs ${colors.text}`}>+{game.xp_reward || 50} XP</span>
                        <button className={`px-3 py-1 ${colors.btn} rounded-lg text-sm font-medium transition`}>
                          Play
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Leaderboard Preview */}
            <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <span>🏆</span> Top Players This Week
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(gameLeaderboard.length > 0 ? gameLeaderboard.slice(0, 3) : [
                  { rank: 1, username: 'GameMaster99', score: 12500 },
                  { rank: 2, username: 'QuizWhiz', score: 11200 },
                  { rank: 3, username: 'SpeedDemon', score: 10800 },
                ]).map((player, idx) => {
                  const rank = player.rank || idx + 1;
                  const avatars = { 1: '🥇', 2: '🥈', 3: '🥉' };
                  return (
                    <div key={rank} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
                      <span className="text-2xl">{avatars[rank] || `#${rank}`}</span>
                      <div className="flex-1">
                        <div className="font-medium">{player.username || player.name}</div>
                        <div className="text-sm text-slate-400">{(player.score || 0).toLocaleString()} pts</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* Discover / My Communities Tab */
          <div>
            {/* Category Filter Tabs for Discover */}
            {activeTab === 'discover' && communityCategories.length > 1 && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {communityCategories.map((cat) => (
                  <button
                    key={cat.category}
                    onClick={() => setSelectedCommunityCategory(cat.category)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                      selectedCommunityCategory === cat.category
                        ? 'bg-emerald-500 text-black'
                        : 'bg-white/5 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {cat.category === 'all' ? '🌐 All' : cat.category === 'uncategorized' ? '📂 Other' : `${cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}`}
                    <span className="ml-2 text-xs opacity-70">({cat.count})</span>
                  </button>
                ))}
              </div>
            )}
            
            {activeTab === 'my-communities' && myCommunities.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-5xl mb-4">🏠</div>
                <h3 className="text-xl font-bold text-white mb-2">No communities yet</h3>
                <p className="text-slate-400 mb-6">Join or create a community to get started!</p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setActiveTab('discover')}
                    className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
                  >
                    Browse Communities
                  </button>
                  <button
                    onClick={handleCreateCommunity}
                    className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-xl transition-colors"
                  >
                    Create Community
                  </button>
                </div>
              </div>
            ) : displayedCommunities.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <div className="text-5xl mb-4">🔍</div>
                <p>No communities found{searchQuery ? ` for "${searchQuery}"` : selectedCommunityCategory !== 'all' ? ` in "${selectedCommunityCategory}"` : ''}.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {displayedCommunities.map((community) => (
                    <CommunityCard
                      key={community.id}
                      community={community}
                      onClick={handleCommunityClick}
                      isMember={myCommunitiesIds.has(community.id)}
                    />
                  ))}
                </div>
                
                {/* Load More Button for Discover tab */}
                {activeTab === 'discover' && hasMoreCommunities && (
                  <div className="text-center mt-8">
                    <button
                      onClick={loadMoreCommunities}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors disabled:opacity-50"
                    >
                      {loadingMore ? 'Loading...' : 'Load More Communities'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
